import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import styles from './HotelSearchForm.module.scss';
import { SVGMapPin, SVGCalendar, SVGUsers, SVGChevronDown, SVGSearch } from '../assets/PackageSVG';
import { searchKnownCities, type KnownCity } from '../utilities/cityDetection';
import { DateRangePickerWidget } from './DateRangePickerWidget';

export type HotelSearchCriteria = {
    /** A city/region name, or a specific property name — the same single
     * "where to?" box every major hotel site uses. There's no dedicated
     * search-by-hotel-name field on the gateway yet (hotel-cards takes a
     * `city` string for geocoding), so whichever the traveller typed is
     * sent through as that same field either way. */
    destination: string;
    latitude?: number;
    longitude?: number;
    radius?: number;
    /** YYYY-MM-DD */
    checkIn: string;
    checkOut: string;
    rooms: number;
    adults: number;
    children: { age: number }[];
};

type HotelSearchFormProps = {
    /** Collected, validated search criteria — sending it to the backend is
     * the caller's job. */
    onSearch: (criteria: HotelSearchCriteria) => void;
    defaultDestination?: string;
    /** Selections to show on mount -- a search that was already submitted,
     * restored when a saved chat is reopened. Only read once; later
     * changes to it don't reset what the user is editing. */
    initialCriteria?: HotelSearchCriteria;
};

type ActiveField = 'destination' | 'dates' | 'guests' | null;

const MAX_ROOMS = 8;
const MAX_ADULTS = 16;
const MAX_CHILDREN = 8;

const todayISO = () => new Date().toISOString().slice(0, 10);
const addDaysISO = (iso: string, days: number) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

/**
 * Portals its child panel to document.body, positioned to hang below the
 * given anchor (above it, when there's no room below) via `position:
 * fixed`. HotelSearchForm renders inside the chat thread's scrolling
 * message list — a plain `position: absolute` popover gets clipped the
 * moment the anchor is near that container's own edge, since absolute
 * positioning still respects an ancestor's overflow clipping. `fixed` + a
 * portal escapes both the clipping and the stacking context, the same
 * trick FlightSearchForm and Modal.tsx both use.
 */
function FloatingPanel({
    anchorRef,
    align,
    matchWidth,
    children,
}: {
    anchorRef: React.RefObject<HTMLElement>;
    align: 'left' | 'right';
    /** Panel width matches the anchor's width (used for the destination
     * dropdown, which should line up with its input field). */
    matchWidth?: boolean;
    children: React.ReactElement;
}) {
    const panelRef = useRef<HTMLElement | null>(null);
    // The first render is hidden so the panel can be measured before it's
    // placed; the layout effect positions it before anything is painted.
    const [style, setStyle] = useState<React.CSSProperties>({ position: 'fixed', top: 0, left: 0, visibility: 'hidden' });

    useLayoutEffect(() => {
        const update = () => {
            const el = anchorRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const viewportHeight = document.documentElement.clientHeight;
            const panelHeight = panelRef.current?.offsetHeight ?? 0;
            // Open upward when the panel won't fit below the anchor but will
            // above it -- a form sitting at the bottom of the chat thread
            // would otherwise run its calendar off the screen.
            const openUp = panelHeight > viewportHeight - rect.bottom - 8 && panelHeight <= rect.top - 8;
            const next: React.CSSProperties = { position: 'fixed' };
            if (openUp) next.bottom = viewportHeight - rect.top + 8;
            else next.top = rect.bottom + 8;
            if (align === 'left') next.left = rect.left;
            else next.right = window.innerWidth - rect.right;
            if (matchWidth) next.width = rect.width;
            setStyle(next);
        };
        update();
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (panelRef.current) resizeObserver?.observe(panelRef.current);
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [anchorRef, align, matchWidth]);

    return createPortal(
        React.cloneElement(children, {
            ref: panelRef,
            style: { ...style, ...(children.props.style || {}) },
            'data-floating-panel': true,
        }),
        document.body
    );
}

function StepRow({
    label,
    sublabel,
    value,
    min,
    max,
    onChange,
}: {
    label: string;
    sublabel?: string;
    value: number;
    min: number;
    max: number;
    onChange: (next: number) => void;
}) {
    return (
        <div className={styles.stepRow}>
            <span className={styles.stepLabelGroup}>
                <span className={styles.stepLabel}>{label}</span>
                {sublabel && <span className={styles.stepSublabel}>{sublabel}</span>}
            </span>
            <div className={styles.stepControls}>
                <button
                    type="button"
                    className={styles.stepButton}
                    disabled={value <= min}
                    onClick={() => onChange(Math.max(min, value - 1))}
                    aria-label={`decrease ${label}`}
                >
                    −
                </button>
                <span className={styles.stepValue}>{value}</span>
                <button
                    type="button"
                    className={styles.stepButton}
                    disabled={value >= max}
                    onClick={() => onChange(Math.min(max, value + 1))}
                    aria-label={`increase ${label}`}
                >
                    +
                </button>
            </div>
        </div>
    );
}

export function HotelSearchForm({ onSearch, defaultDestination, initialCriteria }: HotelSearchFormProps) {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const destinationFieldRef = useRef<HTMLDivElement | null>(null);
    const datesFieldRef = useRef<HTMLButtonElement | null>(null);
    const guestsBtnRef = useRef<HTMLButtonElement | null>(null);

    const [destination, setDestination] = useState(initialCriteria?.destination ?? defaultDestination ?? '');
    /* Only true once the traveller has picked a suggestion or typed their
       own text -- a default supplied by the caller still counts as "needs
       confirming", same spirit as FlightSearchForm's auto-fill guard, so a
       late-arriving defaultDestination prop doesn't clobber manual edits. */
    const destinationTouchedRef = useRef(Boolean(initialCriteria?.destination));

    const [checkIn, setCheckIn] = useState(initialCriteria?.checkIn ?? '');
    const [checkOut, setCheckOut] = useState(initialCriteria?.checkOut ?? '');
    const [latitude, setLatitude] = useState(initialCriteria?.latitude);
    const [longitude, setLongitude] = useState(initialCriteria?.longitude);
    const [radius, setRadius] = useState(initialCriteria?.radius);

    const [rooms, setRooms] = useState(initialCriteria?.rooms ?? 1);
    const [adults, setAdults] = useState(initialCriteria?.adults ?? 2);
    const [children, setChildren] = useState<{ age: number }[]>(initialCriteria?.children ?? []);

    const [activeField, setActiveField] = useState<ActiveField>(null);
    const [highlight, setHighlight] = useState(0);

    useEffect(() => {
        if (destinationTouchedRef.current) return;
        if (!defaultDestination) return;
        destinationTouchedRef.current = true;
        setDestination(defaultDestination);
    }, [defaultDestination]);

    useEffect(() => {
        const onDocMouseDown = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            // Floating panels (dates/guests/destination dropdown) are
            // portaled to document.body, so they fall outside rootRef's own
            // DOM subtree even while open -- a click inside one must not
            // count as "outside" and close it.
            if (rootRef.current?.contains(target)) return;
            if (target.closest?.('[data-floating-panel]')) return;
            setActiveField(null);
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveField(null);
        };
        document.addEventListener('mousedown', onDocMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onDocMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, []);

    const suggestions = useMemo(() => searchKnownCities(destination, 8), [destination]);
    useEffect(() => setHighlight(0), [destination, activeField]);

    const selectCity = (city: KnownCity) => {
        destinationTouchedRef.current = true;
        setDestination(city.name);
        setLatitude(city.lat);
        setLongitude(city.lng);
        setActiveField(null);
    };

    const setChildCount = (count: number) => {
        setChildren((current) => Array.from({ length: count }, (_, i) => current[i] ?? { age: 8 }));
    };

    const minDate = todayISO();
    const maxDate = addDaysISO(minDate, 500);

    const dateLabel = useMemo(() => {
        if (!checkIn) return 'Add dates';
        const inLabel = format(new Date(`${checkIn}T00:00:00`), 'MMM d');
        if (!checkOut) return `${inLabel} – Select checkout`;
        const outLabel = format(new Date(`${checkOut}T00:00:00`), 'MMM d');
        return `${inLabel} – ${outLabel}`;
    }, [checkIn, checkOut]);

    const guestsLabel = useMemo(() => {
        const parts = [`${rooms} room${rooms > 1 ? 's' : ''}`, `${adults} adult${adults > 1 ? 's' : ''}`];
        if (children.length > 0) parts.push(`${children.length} child${children.length > 1 ? 'ren' : ''}`);
        return parts.join(', ');
    }, [rooms, adults, children.length]);

    const canSearch = Boolean(destination.trim() && checkIn && checkOut);

    /* The button stays at full strength (a faded, disabled one read as
       broken), so an early click takes the traveller to the first field
       that still needs an answer instead of doing nothing. */
    const openFirstMissing = () => {
        if (!destination.trim()) document.getElementById('hotel-destination')?.focus();
        else setActiveField('dates');
    };

    const handleSearch = () => {
        if (!canSearch) {
            openFirstMissing();
            return;
        }
        onSearch({ destination: destination.trim(), latitude, longitude, radius, checkIn, checkOut, rooms, adults, children });
    };

    return (
        <div ref={rootRef} className={styles.root}>
            <div className={styles.fieldsRow}>
                <div className={styles.fieldWrap} ref={destinationFieldRef}>
                    <label className={styles.fieldLabel} htmlFor="hotel-destination">Destination or hotel</label>
                    <div className={styles.destinationInputWrap}>
                        <SVGMapPin className={styles.fieldIcon} aria-hidden="true" />
                        <input
                            id="hotel-destination"
                            className={styles.destinationInput}
                            type="text"
                            autoComplete="off"
                            role="combobox"
                            aria-expanded={activeField === 'destination'}
                            aria-controls="hotel-destination-listbox"
                            placeholder="Where are you going?"
                            value={destination}
                            onFocus={() => setActiveField('destination')}
                            onChange={(e) => {
                                destinationTouchedRef.current = true;
                                setDestination(e.target.value);
                                setLatitude(undefined);
                                setLongitude(undefined);
                                setActiveField('destination');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setActiveField('destination');
                                    setHighlight((i) => Math.min(i + 1, suggestions.length - 1));
                                } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setHighlight((i) => Math.max(i - 1, 0));
                                } else if (e.key === 'Enter') {
                                    const pick = suggestions[highlight];
                                    if (activeField === 'destination' && pick) {
                                        e.preventDefault();
                                        selectCity(pick);
                                    }
                                } else if (e.key === 'Escape') {
                                    setActiveField(null);
                                }
                            }}
                        />
                    </div>

                    {activeField === 'destination' && suggestions.length > 0 && (
                        <FloatingPanel anchorRef={destinationFieldRef} align="left" matchWidth>
                            <ul id="hotel-destination-listbox" role="listbox" className={styles.destinationDropdown}>
                                {suggestions.map((city, idx) => (
                                    <li
                                        key={city.name}
                                        role="option"
                                        aria-selected={destination === city.name}
                                        className={`${styles.destinationOption} ${idx === highlight ? styles.destinationOptionActive : ''}`}
                                        onMouseEnter={() => setHighlight(idx)}
                                        onMouseDown={(e) => { e.preventDefault(); selectCity(city); }}
                                    >
                                        <SVGMapPin className={styles.destinationOptionIcon} aria-hidden="true" />
                                        <span className={styles.destinationOptionLabel}>{city.name}</span>
                                    </li>
                                ))}
                            </ul>
                        </FloatingPanel>
                    )}
                </div>

                <div className={styles.popoverAnchor}>
                    {/* The whole box is the button, label included, so it
                        opens from anywhere you click on it. */}
                    <button
                        ref={datesFieldRef}
                        type="button"
                        className={`${styles.fieldWrap} ${styles.dateField}`}
                        aria-expanded={activeField === 'dates'}
                        onClick={() => setActiveField((f) => (f === 'dates' ? null : 'dates'))}
                    >
                        <span className={styles.fieldLabel}>Check-in – Check-out</span>
                        <span className={`${styles.dateValue} ${checkIn ? '' : styles.dateValueEmpty}`}>
                            <SVGCalendar className={styles.fieldIcon} aria-hidden="true" />
                            {dateLabel}
                        </span>
                    </button>

                    {activeField === 'dates' && (
                        <FloatingPanel anchorRef={datesFieldRef} align="left">
                            <div className={`${styles.popoverPanel} ${styles.datePopoverPanel}`}>
                                <DateRangePickerWidget
                                    labels={{ from: 'Check-in', to: 'Check-out' }}
                                    payload={{
                                        min_date: minDate,
                                        max_date: maxDate,
                                        default_from: checkIn || minDate,
                                        default_to: checkOut || addDaysISO(checkIn || minDate, 2),
                                        submitted_value: checkIn && checkOut
                                            ? { from: checkIn, to: checkOut, nights: 0 }
                                            : undefined,
                                    }}
                                    onSubmit={(value) => {
                                        setCheckIn(value.from);
                                        setCheckOut(value.to);
                                        setActiveField(null);
                                    }}
                                />
                            </div>
                        </FloatingPanel>
                    )}
                </div>

                <div className={styles.popoverAnchor}>
                    <button
                        ref={guestsBtnRef}
                        type="button"
                        className={`${styles.fieldWrap} ${styles.guestsField}`}
                        aria-expanded={activeField === 'guests'}
                        onClick={() => setActiveField((f) => (f === 'guests' ? null : 'guests'))}
                    >
                        <span className={styles.fieldLabel}>Rooms &amp; guests</span>
                        <span className={styles.dateValue}>
                            <SVGUsers className={styles.fieldIcon} aria-hidden="true" />
                            {guestsLabel}
                            <SVGChevronDown className={styles.pillChevron} aria-hidden="true" />
                        </span>
                    </button>

                    {activeField === 'guests' && (
                        <FloatingPanel anchorRef={guestsBtnRef} align="right">
                            <div className={styles.popoverPanel}>
                                <StepRow label="Rooms" value={rooms} min={1} max={MAX_ROOMS} onChange={setRooms} />
                                <StepRow label="Adults" value={adults} min={1} max={MAX_ADULTS} onChange={setAdults} />
                                <StepRow label="Children" sublabel="Ages 0–17" value={children.length} min={0} max={MAX_CHILDREN} onChange={setChildCount} />
                                {children.map((child, index) => (
                                    <div key={index} className={styles.childAgeRow}>
                                        <span className={styles.childAgeLabel}>Child {index + 1} age</span>
                                        <select
                                            className={styles.childAgeSelect}
                                            value={child.age}
                                            onChange={(e) =>
                                                setChildren((current) =>
                                                    current.map((c, i) => (i === index ? { age: Number(e.target.value) } : c))
                                                )
                                            }
                                        >
                                            {Array.from({ length: 18 }, (_, age) => (
                                                <option key={age} value={age}>{age === 0 ? '< 1 year' : `${age} year`}</option>
                                            ))}
                                        </select>
                                    </div>
                                ))}
                                <button type="button" className={styles.popoverDoneBtn} onClick={() => setActiveField(null)}>
                                    Done
                                </button>
                            </div>
                        </FloatingPanel>
                    )}
                </div>

                <button type="button" className={styles.searchButton} aria-disabled={!canSearch} onClick={handleSearch}>
                    <SVGSearch className={styles.searchIcon} aria-hidden="true" />
                    Search hotels
                </button>
            </div>
        </div>
    );
}
